import { useMemo, useState } from "react";
import { Row, Container, Col, Pagination, Spinner } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import UserItemShop from "../../components/UserShopItem/UserItemShop";
import UserShopProfile from "../../components/UserShopProfile/UserShopProfile";
import BackArrow from "../../components/backArrow";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetShopByIdQuery,
  useGetShopItemsQuery,
} from "../../features/shop/shopApiSlice";
import { decodeId } from "../../utils/idObfuscator";

const LIMIT = 15;

const splitItemsByOffer = (items = []) => {
  const regular = [];
  const specialoffer = [];
  const salespromotion = [];

  items.forEach((item) => {
    const hasSpecial = item?.specialoffer && item.specialoffer !== "";
    const hasSales = item?.salespromotion && item.salespromotion !== "";
    if (!hasSpecial && !hasSales) regular.push(item);
    else if (!hasSpecial) salespromotion.push(item);
    else if (!hasSales) specialoffer.push(item);
    else {
      specialoffer.push(item);
      salespromotion.push(item);
    }
  });

  return { regular, specialoffer, salespromotion };
};

const HomeMadeItemUserprofile = () => {
  const { shopid: rawShopid } = useParams();
  const shopid = decodeId(rawShopid);
  const { name } = useSelector((state) => state.user);
  const reduxItems = useSelector((state) => state.itemdata?.itemdata) || [];

  const [page, setPage] = useState(1);
  const [lgShowreport, setLgShowreport] = useState(false);
  const [createShop, setCreateShop] = useState({
    userid: name?._id,
    problem: "",
    desciription: "",
    image: [""],
  });

  const {
    data: shopRes,
    error: shopError,
    isLoading: loadingShop,
  } = useGetShopByIdQuery(shopid, { skip: !shopid });
  const shopData = shopRes?.data?.Shop;

  // Prefer Mongo _id once shop is loaded (items are usually saved with _id)
  const itemsShopId = shopData?._id || shopData?.shopid || shopid;

  const {
    data: itemsRes,
    isLoading: itemsLoading,
    isFetching: itemsFetching,
    isSuccess: itemsSuccess,
  } = useGetShopItemsQuery(
    { shopid: itemsShopId, page, limit: LIMIT },
    { skip: !itemsShopId }
  );

  const reduxForShop = useMemo(() => {
    if (!itemsShopId || !Array.isArray(reduxItems)) return [];
    const ids = new Set(
      [shopData?._id, shopData?.shopid, shopid, itemsShopId]
        .filter(Boolean)
        .map(String)
    );
    return reduxItems.filter((item) => ids.has(String(item?.shopid)));
  }, [reduxItems, shopData?._id, shopData?.shopid, shopid, itemsShopId]);

  const apiRegular = itemsRes?.regular || [];
  const apiSpecial = itemsRes?.specialoffer || [];
  const apiSales = itemsRes?.salespromotion || [];
  const apiHasItems =
    apiRegular.length + apiSpecial.length + apiSales.length > 0;

  const fallback = useMemo(
    () => (apiHasItems ? null : splitItemsByOffer(reduxForShop)),
    [apiHasItems, reduxForShop]
  );

  const itemshow = apiHasItems ? apiRegular : fallback?.regular || [];
  const specialoffer = apiHasItems ? apiSpecial : fallback?.specialoffer || [];
  const salepromotion = apiHasItems ? apiSales : fallback?.salespromotion || [];
  const totalPages = itemsRes?.totalPages || 1;
  const newItemdata = [...itemshow, ...specialoffer, ...salepromotion];

  // Show spinner only on first load — not on background refetch
  const loading = Boolean(itemsShopId) && itemsLoading && !itemsSuccess;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <Container className="user-shop-page">
      <div className="user-shop-mobile-back d-lg-none">
        <BackArrow back="/buy-homemade-products" />
      </div>
      <Row>
        <Col lg="4" className="screenHeight user-shop-profile-column">
          <div className="border b-radius height_100">
            {loadingShop ? (
              <Skeleton height={400} />
            ) : shopError ? (
              <div
                className="p-3 text-center text-danger border rounded bg-light"
                style={{ minHeight: "400px" }}
              >
                <h5>Error Loading Shop</h5>
                <p>
                  Could not load shop profile.{" "}
                  {shopError?.data?.message || "Please try again later."}
                </p>
              </div>
            ) : shopData ? (
              <UserShopProfile
                shopData={shopData}
                shopid={itemsShopId}
                lgShowreport={lgShowreport}
                setLgShowreport={setLgShowreport}
                setCreateShop={setCreateShop}
              />
            ) : (
              <div
                className="p-3 text-center text-muted border rounded bg-light"
                style={{ minHeight: "400px" }}
              >
                <h5>Shop Not Found</h5>
                <p>
                  The shop profile you are looking for does not exist or has
                  been removed.
                </p>
              </div>
            )}
          </div>
        </Col>

        <Col lg="8" className="screenHeight user-shop-items-column">
          <div className="border b-radius">
            <div className="ms-3 d-none d-lg-block">
              <BackArrow back="/buy-homemade-products" />
            </div>

            {loading || (itemsFetching && !itemsSuccess && !newItemdata.length) ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "300px" }}
              >
                <Spinner
                  animation="border"
                  role="status"
                  style={{ color: "#0353A4", width: "48px", height: "48px" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <>
                <UserItemShop
                  shopid={itemsShopId}
                  regular={itemshow}
                  specialoffer={specialoffer}
                  salespromotion={salepromotion}
                  newItemdata={newItemdata}
                  shopData={shopData}
                  isDynamic={true}
                />
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-3 mb-3">
                    <Pagination>
                      <Pagination.First
                        onClick={() => handlePageChange(1)}
                        disabled={page === 1}
                      />
                      <Pagination.Prev
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                      />
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Pagination.Item
                          key={i + 1}
                          active={i + 1 === page}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                      />
                      <Pagination.Last
                        onClick={() => handlePageChange(totalPages)}
                        disabled={page === totalPages}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default HomeMadeItemUserprofile;
